import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  ProductBacklogRow,
  ProductBacklogItemRow,
  isProductBacklogItemRow,
  isSprintRow,
  isAssociatedWithSomeSprint,
} from '../productBacklog'
import { SprintDetail, SprintStatus } from '../../../../lib/functions/sprint'
import { useNumberParser } from '../../../hooks/useNumberParser'
import { ProjectMemberProps } from '../../../../lib/functions/projectMember'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'

export const UNASSIGNED_ASSIGNEE_ID = 'unassigned'

interface AssigneeEstimate {
  assigneeId: string
  assigneeName: string
  totalEstimate: number
  taskCount: number
}

interface SprintEstimate {
  sprintId: string
  sprintName: string
  assigneeEstimates: AssigneeEstimate[]
  totalEstimate: number
  totalTasks: number
}

interface SprintData {
  sprintName: string
  assigneeEstimates: Map<
    string,
    { name: string; totalEstimate: number; taskCount: number }
  >
}

type SprintEstimatesMap = Map<string, SprintData>

export const useEstimatesByAssignee = (
  data: ProductBacklogRow[],
  sprints: SprintDetail[]
) => {
  const [estimatesPopupOpen, setEstimatesPopupOpen] = useState<boolean>(false)
  const [initialSprintId, setInitialSprintId] = useState<string | undefined>(
    undefined
  )
  const { round } = useNumberParser()
  const intl = useIntl()

  /**
   * スプリントデータを取得または初期化
   */
  const getOrInitializeSprintData = useCallback(
    (
      sprintEstimatesMap: SprintEstimatesMap,
      sprintKey: string,
      sprintName: string
    ): SprintData => {
      if (!sprintEstimatesMap.has(sprintKey)) {
        sprintEstimatesMap.set(sprintKey, {
          sprintName,
          assigneeEstimates: new Map(),
        })
      }
      return sprintEstimatesMap.get(sprintKey)!
    },
    []
  )

  /**
   * 担当者の見積もりを更新
   */
  const updateAssigneeEstimate = useCallback(
    (
      sprintData: SprintData,
      assignees: ProjectMemberProps[],
      estimate: number
    ) => {
      if (assignees.length === 0) {
        // 担当者未割り当て
        const assigneeKey = UNASSIGNED_ASSIGNEE_ID
        const current = sprintData.assigneeEstimates.get(assigneeKey) || {
          name: intl.formatMessage({
            id: 'productBacklog.estimatesByAssignee.assigneeUnassigned',
          }),
          totalEstimate: 0,
          taskCount: 0,
        }
        sprintData.assigneeEstimates.set(assigneeKey, {
          name: current.name,
          totalEstimate: current.totalEstimate + estimate,
          taskCount: current.taskCount + 1,
        })
      } else {
        // 複数の担当者がいる場合、見積時間は等分するがタスク数は各担当者に1つずつ
        const estimatePerAssignee = estimate / assignees.length
        assignees.forEach((member: ProjectMemberProps) => {
          const assigneeKey = member.uuid
          const current = sprintData.assigneeEstimates.get(assigneeKey) || {
            name:
              member.name ||
              intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.unknownAssignee',
              }),
            totalEstimate: 0,
            taskCount: 0,
          }
          sprintData.assigneeEstimates.set(assigneeKey, {
            name: current.name,
            totalEstimate: current.totalEstimate + estimatePerAssignee,
            taskCount: current.taskCount + 1,
          })
        })
      }
    },
    [intl]
  )

  /**
   * スプリント未設定の行を処理
   */
  const processUnassignedSprintRow = useCallback(
    (
      sprintEstimatesMap: SprintEstimatesMap,
      row: ProductBacklogItemRow,
      assignees: ProjectMemberProps[]
    ) => {
      const sprintKey = 'unassigned-sprint'
      const sprintData = getOrInitializeSprintData(
        sprintEstimatesMap,
        sprintKey,
        intl.formatMessage({
          id: 'productBacklog.estimatesByAssignee.sprintUnassigned',
        })
      )

      const deliverableEstimate = row.wbsItem.estimatedWorkload?.hour || 0
      updateAssigneeEstimate(sprintData, assignees, deliverableEstimate)
    },
    [getOrInitializeSprintData, updateAssigneeEstimate, intl]
  )

  /**
   * スプリント設定済みの行を処理
   */
  const processAssignedSprintRow = useCallback(
    (
      sprintEstimatesMap: SprintEstimatesMap,
      row: ProductBacklogItemRow,
      assignees: ProjectMemberProps[],
      parentSprintUuid: string,
      parentSprintName?: string
    ) => {
      // スプリントの見積工数を取得
      const sprintProductItemKey = `sprint-${parentSprintUuid}-estimated-hour`
      let sprintEstimatedHour = 0

      const editedValue = row.editedData?.[sprintProductItemKey]
      if (editedValue !== undefined) {
        sprintEstimatedHour = editedValue || 0
      } else {
        const sprintProductItem = row.sprintProductItems?.find(
          spi => spi.sprintUuid === parentSprintUuid
        )
        sprintEstimatedHour = sprintProductItem?.estimatedHour || 0
      }

      // スプリントデータを取得または初期化
      // sprints配列ではなく、親行から渡されたスプリント名を使用
      const sprintName =
        parentSprintName ||
        `${intl.formatMessage({
          id: 'productBacklog.estimatesByAssignee.sprintPrefix',
        })} ${parentSprintUuid}`

      const sprintData = getOrInitializeSprintData(
        sprintEstimatesMap,
        parentSprintUuid,
        sprintName
      )

      updateAssigneeEstimate(sprintData, assignees, sprintEstimatedHour)
    },
    [getOrInitializeSprintData, updateAssigneeEstimate, intl]
  )

  /**
   * 担当者情報を収集（PBI担当者を使用、スプリント未設定時は成果物担当者に）
   */
  const collectAssignees = useCallback(
    (row: ProductBacklogItemRow): ProjectMemberProps[] => {
      const assignees: ProjectMemberProps[] = []
      // PBI担当者を優先、未設定時は成果物担当者をフォールバック
      const assignee = row.assignee ?? row.wbsItem.responsible
      if (assignee) {
        assignees.push({
          uuid: assignee.uuid,
          name: assignee.name,
          iconUrl: assignee.iconUrl,
        })
      }
      return assignees
    },
    []
  )

  /**
   * スプリント見積もりデータを構築
   */
  const buildSprintEstimates = useCallback(
    (sprintEstimatesMap: SprintEstimatesMap): SprintEstimate[] => {
      return Array.from(sprintEstimatesMap.entries())
        .map(([sprintId, sprintData]) => {
          const assigneeEstimates = Array.from(
            sprintData.assigneeEstimates.entries()
          )
            .map(([id, data]) => ({
              assigneeId: id,
              assigneeName: data.name,
              totalEstimate: round(data.totalEstimate) ?? 0,
              taskCount: Math.round(data.taskCount),
            }))
            .sort((a, b) => {
              // 未割り当ては常に一番上に表示
              if (a.assigneeId === UNASSIGNED_ASSIGNEE_ID) return -1
              if (b.assigneeId === UNASSIGNED_ASSIGNEE_ID) return 1
              // それ以外は見積り時間の降順
              return b.totalEstimate - a.totalEstimate
            })

          const totalEstimate = assigneeEstimates.reduce(
            (sum, est) => sum + est.totalEstimate,
            0
          )
          const totalTasks = assigneeEstimates.reduce(
            (sum, est) => sum + est.taskCount,
            0
          )

          const sprint = sprints.find(s => s.uuid === sprintId)

          return {
            sprintId,
            sprintName: sprintData.sprintName,
            assigneeEstimates,
            totalEstimate: round(totalEstimate) ?? 0,
            totalTasks: totalTasks,
            sprintNum: sprint?.num ?? Number.MAX_SAFE_INTEGER,
          }
        })
        .sort((a, b) => a.sprintNum - b.sprintNum)
    },
    [round, sprints]
  )

  const getSprintEstimates = useCallback(
    (includeFinishedAndCanceled: boolean = true): SprintEstimate[] => {
      const sprintEstimatesMap: SprintEstimatesMap = new Map()

      // スプリント名のマップとステータスマップを作成（data配列から）
      const sprintNameMap = new Map<string, string>()
      const sprintStatusMap = new Map<string, SprintStatus>()
      data.forEach(row => {
        if (isSprintRow(row) && row.uuid) {
          sprintNameMap.set(row.uuid, row.name)
          // sprintsから該当するスプリントを取得してステータスを記録
          const sprintDetail = sprints.find(s => s.uuid === row.uuid)
          if (sprintDetail) {
            sprintStatusMap.set(row.uuid, sprintDetail.status)
          }
        }
      })

      data.forEach(row => {
        if (!isProductBacklogItemRow(row) || !row.wbsItem) return

        // DISCARD状態の成果物は集計から除外
        if (row.wbsItem.status === WbsItemStatus.DISCARD) return

        const assignees = collectAssignees(row)
        const parentSprintUuid = row.treeValue[0]
        const hasRealSprint = isAssociatedWithSomeSprint(row)

        if (!parentSprintUuid || !hasRealSprint) {
          // スプリント未設定の処理
          processUnassignedSprintRow(sprintEstimatesMap, row, assignees)
        } else {
          // スプリント設定済みの処理
          // includeFinishedAndCanceledがfalseの場合、FINISHED/CANCELEDスプリントはスキップ
          if (!includeFinishedAndCanceled) {
            const status = sprintStatusMap.get(parentSprintUuid)
            if (
              status === SprintStatus.FINISHED ||
              status === SprintStatus.CANCELED
            ) {
              return
            }
          }

          // data配列から取得したスプリント名を渡す
          const parentSprintName = sprintNameMap.get(parentSprintUuid)
          processAssignedSprintRow(
            sprintEstimatesMap,
            row,
            assignees,
            parentSprintUuid,
            parentSprintName
          )
        }
      })

      // 成果物がないスプリントも空のデータで初期化
      // ただし、includeFinishedAndCanceledがfalseの場合、FINISHED/CANCELEDを除外
      sprintNameMap.forEach((name, uuid) => {
        if (!sprintEstimatesMap.has(uuid)) {
          const status = sprintStatusMap.get(uuid)
          if (!status) return

          if (
            includeFinishedAndCanceled ||
            (status !== SprintStatus.FINISHED &&
              status !== SprintStatus.CANCELED)
          ) {
            sprintEstimatesMap.set(uuid, {
              sprintName: name,
              assigneeEstimates: new Map(),
            })
          }
        }
      })

      return buildSprintEstimates(sprintEstimatesMap)
    },
    [
      data,
      sprints,
      collectAssignees,
      processUnassignedSprintRow,
      processAssignedSprintRow,
      buildSprintEstimates,
    ]
  )

  const openEstimatesPopup = useCallback(() => {
    setInitialSprintId(undefined)
    setEstimatesPopupOpen(true)
  }, [])

  const openEstimatesPopupForSprint = useCallback((sprintId: string) => {
    setInitialSprintId(sprintId)
    setEstimatesPopupOpen(true)
  }, [])

  const closeEstimatesPopup = useCallback(() => {
    setEstimatesPopupOpen(false)
    setInitialSprintId(undefined)
  }, [])

  return {
    estimatesPopupOpen,
    openEstimatesPopup,
    openEstimatesPopupForSprint,
    closeEstimatesPopup,
    getSprintEstimates,
    initialSprintId,
  }
}
